import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: 'What Are Cookies?',
    content: `Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently, to improve user experience, and to provide information to website owners. Cookies do not harm your device and do not contain any personally identifiable information in isolation.`,
  },
  {
    title: 'How We Use Cookies',
    content: `NRG Trading uses cookies to ensure the proper functioning of our website, to understand how visitors interact with our content, and to improve your browsing experience. We do not use cookies to collect personal data for marketing purposes without your explicit consent.`,
  },
  {
    title: 'Types of Cookies We Use',
    items: [
      {
        name: 'Strictly Necessary Cookies',
        description:
          'These cookies are essential for the website to function. They enable core features such as page navigation and access to secure areas. The website cannot function properly without these cookies.',
      },
      {
        name: 'Performance & Analytics Cookies',
        description:
          'These cookies collect anonymised information about how visitors use our website — for example, which pages are visited most often. This data helps us improve the way our website works.',
      },
      {
        name: 'Functionality Cookies',
        description:
          'These cookies allow the website to remember choices you make (such as your preferred language or region) and provide enhanced, more personalised features.',
      },
      {
        name: 'Third-Party Cookies',
        description:
          'In some cases, we may embed content or tools provided by third parties (such as video players or map services). These third parties may set their own cookies. NRG Trading has no control over these cookies.',
      },
    ],
  },
  {
    title: 'Managing Cookies',
    content: `You have the right to accept or decline cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies if you prefer. Please note that disabling cookies may affect the functionality of this website and limit your ability to use certain features.`,
    sub: `To manage cookies in your browser, please refer to your browser&apos;s help documentation:`,
    browsers: ['Google Chrome', 'Mozilla Firefox', 'Safari', 'Microsoft Edge', 'Opera'],
  },
  {
    title: 'Cookies from Third-Party Services',
    content: `Our website may include links to third-party websites or integrated services. These third parties operate their own cookie policies and we encourage you to review those policies directly. NRG Trading is not responsible for the content or privacy practices of any third-party website.`,
  },
  {
    title: 'Updates to This Policy',
    content: `NRG Trading reserves the right to update this Cookie Policy at any time to reflect changes in technology, legislation, or our business operations. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.`,
  },
  {
    title: 'Contact Us',
    content: `If you have any questions or concerns about our use of cookies, please contact us at:`,
    contact: true,
  },
];

export default function CookiePolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-[#061828] text-white font-['Inter',sans-serif]">
      {/* Top Nav Bar */}
      <div className="border-b border-white/10 sticky top-0 z-50 bg-[#061828]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="text-white font-bold text-base tracking-wide hover:text-white/80 transition-colors cursor-pointer">
            NRG Trading
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/50 text-sm hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-[#0a2030] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <p className="text-[#e87c2b] text-xs font-bold tracking-widest uppercase mb-4">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">Cookie Policy</h1>
          <p className="text-white/50 text-sm">
            Effective Date: <span className="text-white/70">1 January 2025</span>
          </p>
          <p className="text-white/50 text-sm mt-1">
            Last Updated: <span className="text-white/70">27 March 2026</span>
          </p>
          <p className="text-white/50 text-sm leading-relaxed mt-6 max-w-2xl">
            This Cookie Policy explains how NRG Trading (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) uses cookies and similar tracking technologies
            on our website. By continuing to use our website, you consent to the use of cookies as described in this policy.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14 md:py-20 space-y-14">
        {sections.map((sec) => (
          <section key={sec.title}>
            <h2 className="text-lg font-bold text-white mb-4 pb-3 border-b border-white/10">{sec.title}</h2>

            {sec.content && (
              <p className="text-white/60 text-sm leading-relaxed">{sec.content}</p>
            )}

            {sec.items && (
              <div className="space-y-5 mt-4">
                {sec.items.map((item) => (
                  <div key={item.name} className="bg-white/5 rounded-lg p-5">
                    <p className="text-white text-sm font-semibold mb-2">{item.name}</p>
                    <p className="text-white/55 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            )}

            {sec.sub && (
              <p className="text-white/60 text-sm leading-relaxed mt-4">{sec.sub}</p>
            )}

            {sec.browsers && (
              <ul className="mt-3 space-y-1">
                {sec.browsers.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-white/50 text-sm">
                    <span className="w-1 h-1 rounded-full bg-[#e87c2b] inline-block" />
                    {b}
                  </li>
                ))}
              </ul>
            )}

            {sec.contact && (
              <div className="mt-4 bg-white/5 rounded-lg p-5 text-sm text-white/60 leading-relaxed space-y-1">
                <p className="text-white font-semibold">NRG Trading</p>
                <p>Email: <a href="mailto:info@nrgtrd.com" className="text-[#e87c2b] hover:underline cursor-pointer">info@nrgtrd.com</a></p>
              </div>
            )}
          </section>
        ))}
      </div>

      {/* Footer Strip */}
      <div className="border-t border-white/10 bg-[#061828]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} NRG Trading. All rights reserved.</p>
          <Link to="/" className="text-white/30 text-xs hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">
            Return to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
}
